import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { computed, inject, Injectable, PLATFORM_ID, signal } from "@angular/core";

import { environment } from "@azm/environments/environment";

const SCRIPT_ID = "azm-sign-language-provider";
const ROOT_ATTRIBUTE = "data-azm-a11y-sign-language";

@Injectable({
	providedIn: "root",
})
export class SignLanguageService {
	readonly #platformId = inject(PLATFORM_ID);
	readonly #document = inject(DOCUMENT);

	readonly enabled = signal(false);
	readonly loadFailed = signal(false);

	readonly isAvailable = computed(() => {
		const config = environment.signLanguage;
		return config?.enabled === true && !!config.scriptUrl;
	});

	#scriptLoaded = false;

	toggle(): void {
		if (this.enabled()) {
			this.disable();
			return;
		}

		this.enable();
	}

	enable(): void {
		if (!isPlatformBrowser(this.#platformId) || !this.isAvailable()) {
			return;
		}

		this.loadFailed.set(false);
		this.enabled.set(true);
		this.#document.documentElement.setAttribute(ROOT_ATTRIBUTE, "");
		this.#loadProviderScript();
	}

	disable(): void {
		if (!isPlatformBrowser(this.#platformId)) {
			return;
		}

		this.enabled.set(false);
		this.#document.documentElement.removeAttribute(ROOT_ATTRIBUTE);
	}

	#loadProviderScript(): void {
		if (this.#scriptLoaded || this.#document.getElementById(SCRIPT_ID)) {
			this.#scriptLoaded = true;
			return;
		}

		const config = environment.signLanguage;
		if (!config?.scriptUrl) {
			return;
		}

		const script = this.#document.createElement("script");
		script.id = SCRIPT_ID;
		script.src = config.scriptUrl;
		script.async = true;

		if (config.apiKey) {
			script.dataset["apiKey"] = config.apiKey;
		}

		script.onload = () => {
			this.#scriptLoaded = true;
		};

		script.onerror = () => {
			this.#scriptLoaded = false;
			this.loadFailed.set(true);
			this.disable();
			script.remove();
		};

		this.#document.body.appendChild(script);
	}
}
